import { Unit } from './types';
import { vocabLesson } from './vocab';

/**
 * The words of the sectors EPS workers are hired into besides manufacturing:
 * construction, agriculture and livestock, and fishery. Written for Prepyo
 * from everyday site, farm and boat vocabulary.
 */

export const CONSTRUCTION: Unit = {
  id: 'construction',
  number: 6,
  title: 'Construction',
  description: 'The site, its materials and machines, the work itself, and staying safe up high.',
  emoji: '🏗️',
  lessons: [
    vocabLesson('site', 'The building site', 'Site, building, wall, floor and stairs', [
      { ko: '건설 현장', roman: 'geon-seol hyeon-jang', en: 'construction site', picture: '🏗️' },
      { ko: '건물', roman: 'geon-mul', en: 'building', picture: '🏢' },
      { ko: '벽', roman: 'byeok', en: 'wall' },
      { ko: '바닥', roman: 'ba-dak', en: 'floor' },
      { ko: '천장', roman: 'cheon-jang', en: 'ceiling' },
      { ko: '계단', roman: 'gye-dan', en: 'stairs', picture: '🪜' },
      { ko: '지붕', roman: 'ji-bung', en: 'roof', picture: '🏠' },
      { ko: '기둥', roman: 'gi-dung', en: 'pillar, column' },
      { ko: '창고', roman: 'chang-go', en: 'storehouse' },
      { ko: '컨테이너', roman: 'keon-te-i-neo', en: 'container', picture: '🚛' },
    ]),
    vocabLesson('building-materials', 'Building materials', 'Cement, sand, brick, rebar and timber', [
      { ko: '시멘트', roman: 'si-men-teu', en: 'cement' },
      { ko: '모래', roman: 'mo-rae', en: 'sand' },
      { ko: '벽돌', roman: 'byeok-dol', en: 'brick', picture: '🧱' },
      { ko: '철근', roman: 'cheol-geun', en: 'rebar, steel bar' },
      { ko: '목재', roman: 'mok-jae', en: 'timber', picture: '🪵' },
      { ko: '합판', roman: 'hap-pan', en: 'plywood' },
      { ko: '콘크리트', roman: 'kon-keu-ri-teu', en: 'concrete' },
      { ko: '타일', roman: 'ta-il', en: 'tile' },
      { ko: '자갈', roman: 'ja-gal', en: 'gravel', picture: '🪨' },
      { ko: '못', roman: 'mot', en: 'nail', picture: '📌' },
    ]),
    vocabLesson('equipment', 'Equipment', 'Shovel, saw, drill, crane and scaffolding', [
      { ko: '삽', roman: 'sap', en: 'shovel' },
      { ko: '곡괭이', roman: 'gok-gwaeng-i', en: 'pickaxe', picture: '⛏️' },
      { ko: '톱', roman: 'top', en: 'saw', picture: '🪚' },
      { ko: '드릴', roman: 'deu-ril', en: 'drill' },
      { ko: '줄자', roman: 'jul-ja', en: 'tape measure', picture: '📏' },
      { ko: '손수레', roman: 'son-su-re', en: 'wheelbarrow' },
      { ko: '크레인', roman: 'keu-re-in', en: 'crane' },
      { ko: '굴착기', roman: 'gul-chak-gi', en: 'excavator', picture: '🚜' },
      { ko: '비계', roman: 'bi-gye', en: 'scaffolding' },
      { ko: '레미콘', roman: 're-mi-kon', en: 'concrete mixer truck', picture: '🚚' },
    ]),
    vocabLesson('site-work', 'Site work', 'Dig, stack, mix, pour and measure', [
      { ko: '파요', roman: 'pa-yo', en: 'dig' },
      { ko: '쌓아요', roman: 'ssa-a-yo', en: 'stack, pile up' },
      { ko: '섞어요', roman: 'seo-kkeo-yo', en: 'mix' },
      { ko: '부어요', roman: 'bu-eo-yo', en: 'pour' },
      { ko: '발라요', roman: 'bal-la-yo', en: 'spread, plaster' },
      { ko: '칠해요', roman: 'chil-hae-yo', en: 'paint', picture: '🖌️' },
      { ko: '묶어요', roman: 'mu-kkeo-yo', en: 'tie' },
      { ko: '재요', roman: 'jae-yo', en: 'measure' },
      { ko: '박아요', roman: 'ba-ga-yo', en: 'hammer in' },
      { ko: '올려요', roman: 'ol-lyeo-yo', en: 'raise, lift up' },
    ]),
    vocabLesson('site-safety', 'Site safety', 'Harness, falls, danger zones and electric shock', [
      { ko: '안전벨트', roman: 'an-jeon-bel-teu', en: 'safety harness' },
      { ko: '추락', roman: 'chu-rak', en: 'fall (from a height)' },
      { ko: '낙하물', roman: 'na-ka-mul', en: 'falling objects' },
      { ko: '위험 구역', roman: 'wi-heom gu-yeok', en: 'danger zone', picture: '⚠️' },
      { ko: '작업 중지', roman: 'ja-geop jung-ji', en: 'stop work', picture: '🛑' },
      { ko: '안전 교육', roman: 'an-jeon gyo-yuk', en: 'safety training' },
      { ko: '감전', roman: 'gam-jeon', en: 'electric shock', picture: '⚡' },
      { ko: '보호 안경', roman: 'bo-ho an-gyeong', en: 'safety glasses', picture: '🥽' },
      { ko: '귀마개', roman: 'gwi-ma-gae', en: 'earplugs' },
      { ko: '소음', roman: 'so-eum', en: 'noise', picture: '🔊' },
    ]),
  ],
};

export const FARMING: Unit = {
  id: 'farming',
  number: 7,
  title: 'Agriculture & Livestock',
  description: 'Crops and fields, farm tools, animals and the daily work of a farm.',
  emoji: '🌾',
  lessons: [
    vocabLesson('crops', 'Crops', 'Rice, cabbage, chili, garlic and strawberries', [
      { ko: '쌀', roman: 'ssal', en: 'rice (grain)', picture: '🍚' },
      { ko: '채소', roman: 'chae-so', en: 'vegetables', picture: '🥬' },
      { ko: '배추', roman: 'bae-chu', en: 'napa cabbage' },
      { ko: '고추', roman: 'go-chu', en: 'chili pepper', picture: '🌶️' },
      { ko: '마늘', roman: 'ma-neul', en: 'garlic', picture: '🧄' },
      { ko: '양파', roman: 'yang-pa', en: 'onion', picture: '🧅' },
      { ko: '감자', roman: 'gam-ja', en: 'potato', picture: '🥔' },
      { ko: '딸기', roman: 'ttal-gi', en: 'strawberry', picture: '🍓' },
      { ko: '포도', roman: 'po-do', en: 'grapes', picture: '🍇' },
      { ko: '버섯', roman: 'beo-seot', en: 'mushroom', picture: '🍄' },
    ]),
    vocabLesson('farm-work', 'Farm work', 'Field, paddy, greenhouse, plant and harvest', [
      { ko: '밭', roman: 'bat', en: 'field' },
      { ko: '논', roman: 'non', en: 'rice paddy', picture: '🌾' },
      { ko: '비닐하우스', roman: 'bi-nil-ha-u-seu', en: 'greenhouse' },
      { ko: '씨앗', roman: 'ssi-at', en: 'seed', picture: '🌱' },
      { ko: '심어요', roman: 'si-meo-yo', en: 'plant' },
      { ko: '물을 줘요', roman: 'mu-reul jwo-yo', en: 'water (the plants)', picture: '💧' },
      { ko: '수확해요', roman: 'su-hwa-kae-yo', en: 'harvest' },
      { ko: '비료', roman: 'bi-ryo', en: 'fertiliser' },
      { ko: '농약', roman: 'nong-yak', en: 'pesticide' },
      { ko: '잡초', roman: 'jap-cho', en: 'weeds' },
    ]),
    vocabLesson('farm-tools', 'Farm tools', 'Tractor, hoe, sickle, basket and boots', [
      { ko: '트랙터', roman: 'teu-raek-teo', en: 'tractor', picture: '🚜' },
      { ko: '경운기', roman: 'gyeong-un-gi', en: 'power tiller' },
      { ko: '호미', roman: 'ho-mi', en: 'hand hoe' },
      { ko: '낫', roman: 'nat', en: 'sickle' },
      { ko: '바구니', roman: 'ba-gu-ni', en: 'basket', picture: '🧺' },
      { ko: '호스', roman: 'ho-seu', en: 'hose' },
      { ko: '분무기', roman: 'bun-mu-gi', en: 'sprayer' },
      { ko: '장화', roman: 'jang-hwa', en: 'rubber boots', picture: '🥾' },
      { ko: '수레', roman: 'su-re', en: 'cart', picture: '🛒' },
      { ko: '전지가위', roman: 'jeon-ji-ga-wi', en: 'pruning shears', picture: '✂️' },
    ]),
    vocabLesson('animals', 'Animals', 'Cow, pig, chicken, duck and goat', [
      { ko: '소', roman: 'so', en: 'cow', picture: '🐄' },
      { ko: '돼지', roman: 'dwae-ji', en: 'pig', picture: '🐖' },
      { ko: '닭', roman: 'dak', en: 'chicken', picture: '🐔' },
      { ko: '오리', roman: 'o-ri', en: 'duck', picture: '🦆' },
      { ko: '염소', roman: 'yeom-so', en: 'goat', picture: '🐐' },
      { ko: '말', roman: 'mal', en: 'horse', picture: '🐎' },
      { ko: '양', roman: 'yang', en: 'sheep', picture: '🐑' },
      { ko: '달걀', roman: 'dal-gyal', en: 'egg', picture: '🥚' },
      { ko: '축사', roman: 'chuk-sa', en: 'livestock barn' },
      { ko: '사료', roman: 'sa-ryo', en: 'animal feed' },
    ]),
    vocabLesson('livestock-work', 'Livestock work', 'Feed, clean, milk, disinfect and vaccinate', [
      { ko: '먹이를 줘요', roman: 'meo-gi-reul jwo-yo', en: 'feed (the animals)' },
      { ko: '청소해요', roman: 'cheong-so-hae-yo', en: 'clean', picture: '🧹' },
      { ko: '우유를 짜요', roman: 'u-yu-reul jja-yo', en: 'milk (a cow)', picture: '🥛' },
      { ko: '소독해요', roman: 'so-do-kae-yo', en: 'disinfect' },
      { ko: '예방 주사', roman: 'ye-bang ju-sa', en: 'vaccination', picture: '💉' },
      { ko: '수의사', roman: 'su-ui-sa', en: 'vet' },
      { ko: '환기', roman: 'hwan-gi', en: 'ventilation' },
      { ko: '온도', roman: 'on-do', en: 'temperature', picture: '🌡️' },
      { ko: '병', roman: 'byeong', en: 'disease' },
      { ko: '출하', roman: 'chul-ha', en: 'shipping (to market)' },
    ]),
  ],
};

export const FISHERY: Unit = {
  id: 'fishery',
  number: 8,
  title: 'Fishery',
  description: 'Boats and the sea, the catch, the work on deck, the weather, and fish farms.',
  emoji: '🎣',
  lessons: [
    vocabLesson('sea-boat', 'Sea and boat', 'Sea, boat, harbour, captain and crew', [
      { ko: '바다', roman: 'ba-da', en: 'sea', picture: '🌊' },
      { ko: '배', roman: 'bae', en: 'boat, ship', picture: '🚢', note: '배 also means “stomach” and “pear”. The sentence makes it clear.' },
      { ko: '항구', roman: 'hang-gu', en: 'harbour' },
      { ko: '선장', roman: 'seon-jang', en: 'captain' },
      { ko: '선원', roman: 'seo-nwon', en: 'crew member' },
      { ko: '그물', roman: 'geu-mul', en: 'fishing net', picture: '🕸️' },
      { ko: '낚싯대', roman: 'nak-ssit-dae', en: 'fishing rod', picture: '🎣' },
      { ko: '갑판', roman: 'gap-pan', en: 'deck' },
      { ko: '구명조끼', roman: 'gu-myeong-jo-kki', en: 'life jacket', picture: '🦺' },
      { ko: '닻', roman: 'dat', en: 'anchor', picture: '⚓' },
    ]),
    vocabLesson('seafood', 'Fish and seafood', 'Fish, squid, shrimp, crab and octopus', [
      { ko: '생선', roman: 'saeng-seon', en: 'fish (as food)', picture: '🐟' },
      { ko: '오징어', roman: 'o-jing-eo', en: 'squid', picture: '🦑' },
      { ko: '새우', roman: 'sae-u', en: 'shrimp', picture: '🦐' },
      { ko: '게', roman: 'ge', en: 'crab', picture: '🦀' },
      { ko: '조개', roman: 'jo-gae', en: 'clam', picture: '🐚' },
      { ko: '굴', roman: 'gul', en: 'oyster', picture: '🦪' },
      { ko: '김', roman: 'gim', en: 'laver (seaweed)' },
      { ko: '멸치', roman: 'myeol-chi', en: 'anchovy' },
      { ko: '고등어', roman: 'go-deung-eo', en: 'mackerel' },
      { ko: '문어', roman: 'mu-neo', en: 'octopus', picture: '🐙' },
    ]),
    vocabLesson('fishing-work', 'Fishing work', 'Cast and haul the net, sort, freeze and weigh', [
      { ko: '그물을 던져요', roman: 'geu-mu-reul deon-jyeo-yo', en: 'cast the net' },
      { ko: '끌어올려요', roman: 'kkeu-reo-ol-lyeo-yo', en: 'haul up' },
      { ko: '잡아요', roman: 'ja-ba-yo', en: 'catch' },
      { ko: '분류해요', roman: 'bul-lyu-hae-yo', en: 'sort' },
      { ko: '손질해요', roman: 'son-jil-hae-yo', en: 'clean and trim (fish)' },
      { ko: '얼려요', roman: 'eol-lyeo-yo', en: 'freeze' },
      { ko: '얼음', roman: 'eo-reum', en: 'ice', picture: '🧊' },
      { ko: '냉동 창고', roman: 'naeng-dong chang-go', en: 'cold store' },
      { ko: '무게', roman: 'mu-ge', en: 'weight', picture: '⚖️' },
      { ko: '출항', roman: 'chul-hang', en: 'setting sail' },
    ]),
    vocabLesson('sea-weather', 'Weather at sea', 'Waves, typhoon, fog and storm', [
      { ko: '파도', roman: 'pa-do', en: 'waves', picture: '🌊' },
      { ko: '태풍', roman: 'tae-pung', en: 'typhoon', picture: '🌀' },
      { ko: '안개', roman: 'an-gae', en: 'fog', picture: '🌫️' },
      { ko: '폭풍', roman: 'pok-pung', en: 'storm', picture: '⛈️' },
      { ko: '바람이 세요', roman: 'ba-ra-mi se-yo', en: 'the wind is strong', picture: '🌬️' },
      { ko: '물살', roman: 'mul-sal', en: 'current' },
      { ko: '수영', roman: 'su-yeong', en: 'swimming', picture: '🏊' },
      { ko: '구명보트', roman: 'gu-myeong-bo-teu', en: 'lifeboat' },
      { ko: '일기 예보', roman: 'il-gi ye-bo', en: 'weather forecast' },
      { ko: '귀항', roman: 'gwi-hang', en: 'returning to port' },
    ]),
    vocabLesson('fish-farm', 'Fish farms and processing', 'Fish farm, tank, oxygen, apron and hygiene', [
      { ko: '양식장', roman: 'yang-sik-jang', en: 'fish farm' },
      { ko: '수조', roman: 'su-jo', en: 'water tank' },
      { ko: '수온', roman: 'su-on', en: 'water temperature' },
      { ko: '산소', roman: 'san-so', en: 'oxygen' },
      { ko: '치어', roman: 'chi-eo', en: 'young fish' },
      { ko: '가공 공장', roman: 'ga-gong gong-jang', en: 'processing plant' },
      { ko: '작업대', roman: 'ja-geop-dae', en: 'workbench' },
      { ko: '앞치마', roman: 'ap-chi-ma', en: 'apron' },
      { ko: '위생', roman: 'wi-saeng', en: 'hygiene', picture: '🧼' },
      { ko: '포장해요', roman: 'po-jang-hae-yo', en: 'pack' },
    ]),
  ],
};
